import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Star, Add } from '@mui/icons-material';
import { LoyaltyProgram } from '../../types';

interface AddProgramDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (program: any) => void;
  programType: 'airline' | 'hotel';
}

interface ProgramTemplate {
  name: string;
  logo: string;
  currency: string;
  color: string;
  region?: 'US' | 'India';
  tiers: {
    name: string;
    requirements: string;
    benefits: string[];
    color: string;
  }[];
}

const AddProgramDialog: React.FC<AddProgramDialogProps> = ({
  open,
  onClose,
  onAdd,
  programType,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedProgram, setSelectedProgram] = useState<ProgramTemplate | null>(null);
  const [selectedTier, setSelectedTier] = useState(0);
  const [regionFilter, setRegionFilter] = useState<'all' | 'US' | 'India'>('all');
  const [customProgram, setCustomProgram] = useState({
    name: '',
    logo: '',
    currency: 'Points',
  });
  const [programDetails, setProgramDetails] = useState({
    points: '',
    tier: '',
    accountNumber: '',
  });

  const steps = ['Select Program', 'Program Details', 'Confirm'];

  // Popular program templates based on type
  const getPopularPrograms = (): ProgramTemplate[] => {
    if (programType === 'airline') {
      return [
        // US Airlines
        {
          name: 'American AAdvantage',
          logo: '🇺🇸',
          currency: 'Miles',
          color: '#c41e3a',
          region: 'US',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn miles', 'Book awards', 'Partner benefits'],
              color: '#666',
            },
            {
              name: 'Gold',
              requirements: '40,000 miles or 30 segments',
              benefits: ['Priority check-in', 'Free checked bag', 'Preferred seats'],
              color: '#ffd700',
            },
            {
              name: 'Platinum',
              requirements: '75,000 miles or 60 segments',
              benefits: ['Complimentary upgrades', 'Priority boarding', 'Lounge access'],
              color: '#e5e4e2',
            },
            {
              name: 'Executive Platinum',
              requirements: '200,000 miles or 120 segments',
              benefits: ['Guaranteed upgrades', 'Premium lounge access', 'Bonus earning'],
              color: '#1f1f1f',
            },
          ],
        },
        {
          name: 'Delta SkyMiles',
          logo: '✈️',
          currency: 'Miles',
          color: '#003366',
          region: 'US',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn miles', 'Book awards', 'Partner benefits'],
              color: '#666',
            },
            {
              name: 'Silver Medallion',
              requirements: '25,000 MQMs or 30 segments',
              benefits: ['Priority boarding', 'Free checked bag', 'Preferred seats'],
              color: '#c0c0c0',
            },
            {
              name: 'Gold Medallion',
              requirements: '50,000 MQMs or 60 segments',
              benefits: ['Complimentary upgrades', 'Sky Club access', 'Priority check-in'],
              color: '#ffd700',
            },
            {
              name: 'Platinum Medallion',
              requirements: '75,000 MQMs or 100 segments',
              benefits: ['Choice benefits', 'Enhanced upgrades', 'Premium support'],
              color: '#e5e4e2',
            },
          ],
        },
        {
          name: 'United MileagePlus',
          logo: '🌐',
          currency: 'Miles',
          color: '#0e2f5a',
          region: 'US',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn miles', 'Book awards', 'Partner benefits'],
              color: '#666',
            },
            {
              name: 'Premier Silver',
              requirements: '25,000 PQPs or 24 PQFs',
              benefits: ['Priority boarding', 'Free checked bag', 'Preferred seats'],
              color: '#c0c0c0',
            },
            {
              name: 'Premier Gold',
              requirements: '50,000 PQPs or 36 PQFs',
              benefits: ['Complimentary upgrades', 'Club access', 'Priority services'],
              color: '#ffd700',
            },
            {
              name: 'Premier Platinum',
              requirements: '75,000 PQPs or 54 PQFs',
              benefits: ['Enhanced benefits', 'Premium upgrades', 'Global services'],
              color: '#e5e4e2',
            },
          ],
        },
        
        // Indian Airlines
        {
          name: 'Air India Flying Returns',
          logo: '🇮🇳',
          currency: 'Miles',
          color: '#FF671F',
          region: 'India',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn miles', 'Book awards', 'Star Alliance benefits'],
              color: '#666',
            },
            {
              name: 'Silver',
              requirements: '25,000 miles or 20 segments',
              benefits: ['Priority check-in', 'Extra baggage', 'Lounge access'],
              color: '#c0c0c0',
            },
            {
              name: 'Gold',
              requirements: '50,000 miles or 40 segments',
              benefits: ['Priority boarding', 'Complimentary upgrades', 'Premium lounge'],
              color: '#ffd700',
            },
            {
              name: 'Platinum',
              requirements: '100,000 miles or 60 segments',
              benefits: ['Guaranteed upgrades', 'Concierge service', 'Fast track'],
              color: '#e5e4e2',
            },
          ],
        },
        {
          name: 'IndiGo 6E Rewards',
          logo: '🇮🇳',
          currency: 'Points',
          color: '#001E62',
          region: 'India',
          tiers: [
            {
              name: 'Blue',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Exclusive fares', 'Fast booking'],
              color: '#001E62',
            },
            {
              name: 'Blue Plus',
              requirements: '25,000 points',
              benefits: ['Extra points', 'Free meal', 'Priority check-in'],
              color: '#0052CC',
            },
          ],
        },
        {
          name: 'Vistara Club Vistara',
          logo: '🇮🇳',
          currency: 'Points (CV Points)',
          color: '#7B2869',
          region: 'India',
          tiers: [
            {
              name: 'Base',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Book awards', 'Member offers'],
              color: '#666',
            },
            {
              name: 'Silver',
              requirements: '10,000 tier points',
              benefits: ['Priority check-in', 'Extra baggage', 'Lounge access'],
              color: '#c0c0c0',
            },
            {
              name: 'Gold',
              requirements: '25,000 tier points',
              benefits: ['Complimentary upgrades', 'Fast track', 'Premium lounge'],
              color: '#ffd700',
            },
          ],
        },
        {
          name: 'SpiceClub',
          logo: '🇮🇳',
          currency: 'SpicePoints',
          color: '#FF0000',
          region: 'India',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Book flights', 'Partner benefits'],
              color: '#666',
            },
            {
              name: 'Elite',
              requirements: '30,000 SpicePoints',
              benefits: ['Priority boarding', 'Extra baggage', 'Lounge access'],
              color: '#FFD700',
            },
          ],
        },
        {
          name: 'Go First GoMiles',
          logo: '🇮🇳',
          currency: 'GoMiles',
          color: '#FFA500',
          region: 'India',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn miles', 'Free booking', 'Partner rewards'],
              color: '#666',
            },
          ],
        },
      ];
    } else {
      return [
        // International Hotels
        {
          name: 'Marriott Bonvoy',
          logo: '🏨',
          currency: 'Points',
          color: '#8B0000',
          region: 'US',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Free Wi-Fi', 'Mobile check-in'],
              color: '#666',
            },
            {
              name: 'Silver Elite',
              requirements: '10 nights',
              benefits: ['Late checkout', 'Preferred room', 'Bonus points'],
              color: '#c0c0c0',
            },
            {
              name: 'Gold Elite',
              requirements: '25 nights',
              benefits: ['Room upgrades', 'Lounge access', 'Free breakfast'],
              color: '#ffd700',
            },
            {
              name: 'Platinum Elite',
              requirements: '50 nights',
              benefits: ['Suite upgrades', 'Club lounge', 'Choice benefit'],
              color: '#e5e4e2',
            },
          ],
        },
        {
          name: 'Hilton Honors',
          logo: '🏢',
          currency: 'Points',
          color: '#0066cc',
          region: 'US',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Digital key', 'Member rate'],
              color: '#666',
            },
            {
              name: 'Silver',
              requirements: '4 stays or 10 nights',
              benefits: ['Priority room assignment', 'Bonus points', 'Space available upgrades'],
              color: '#c0c0c0',
            },
            {
              name: 'Gold',
              requirements: '20 stays or 40 nights',
              benefits: ['Room upgrades', 'Executive lounge', 'Free breakfast'],
              color: '#ffd700',
            },
            {
              name: 'Diamond',
              requirements: '30 stays or 60 nights',
              benefits: ['Premium Wi-Fi', 'Executive lounge', '48-hour room guarantee'],
              color: '#b9f2ff',
            },
          ],
        },
        {
          name: 'World of Hyatt',
          logo: '🌍',
          currency: 'Points',
          color: '#8b4513',
          region: 'US',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Member rates', 'Late checkout'],
              color: '#666',
            },
            {
              name: 'Discoverist',
              requirements: '10 nights or 25,000 points',
              benefits: ['Bonus points', 'Premium Wi-Fi', 'Room upgrades'],
              color: '#4169e1',
            },
            {
              name: 'Explorist',
              requirements: '30 nights or 50,000 points',
              benefits: ['Club lounge access', 'Free breakfast', 'Late checkout'],
              color: '#32cd32',
            },
            {
              name: 'Globalist',
              requirements: '60 nights or 100,000 points',
              benefits: ['Suite upgrades', 'Guest of Honor', 'Club access'],
              color: '#dc143c',
            },
          ],
        },
        
        // Indian Hotels
        {
          name: 'Taj InnerCircle',
          logo: '🇮🇳',
          currency: 'Points',
          color: '#8B4513',
          region: 'India',
          tiers: [
            {
              name: 'Silver',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Member rates', 'Exclusive offers'],
              color: '#c0c0c0',
            },
            {
              name: 'Gold',
              requirements: '10 stays or 20 nights',
              benefits: ['Room upgrades', 'Late checkout', 'Bonus points'],
              color: '#ffd700',
            },
            {
              name: 'Platinum',
              requirements: '25 stays or 50 nights',
              benefits: ['Suite upgrades', 'Club lounge', 'Priority services'],
              color: '#e5e4e2',
            },
          ],
        },
        {
          name: 'ITC Hotel One',
          logo: '🇮🇳',
          currency: 'Points',
          color: '#D4AF37',
          region: 'India',
          tiers: [
            {
              name: 'Green',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Member discounts', 'Special offers'],
              color: '#228B22',
            },
            {
              name: 'Platinum',
              requirements: '12 stays or 24 nights',
              benefits: ['Room upgrades', 'Complimentary breakfast', 'Late checkout'],
              color: '#e5e4e2',
            },
          ],
        },
        {
          name: 'Club Mahindra',
          logo: '🇮🇳',
          currency: 'Points',
          color: '#DC143C',
          region: 'India',
          tiers: [
            {
              name: 'Member',
              requirements: 'Membership',
              benefits: ['Resort access', 'Exchange weeks', 'Holiday offers'],
              color: '#666',
            },
          ],
        },
        {
          name: 'Oberoi One',
          logo: '🇮🇳',
          currency: 'Points',
          color: '#800020',
          region: 'India',
          tiers: [
            {
              name: 'Silver',
              requirements: 'Join for free',
              benefits: ['Earn points', 'Room upgrades', 'Special amenities'],
              color: '#c0c0c0',
            },
            {
              name: 'Gold',
              requirements: '10 stays',
              benefits: ['Guaranteed upgrades', 'Complimentary breakfast', 'Priority check-in'],
              color: '#ffd700',
            },
          ],
        },
        {
          name: 'Lemon Tree Smiles',
          logo: '🇮🇳',
          currency: 'Smiles',
          color: '#ADFF2F',
          region: 'India',
          tiers: [
            {
              name: 'Member',
              requirements: 'Join for free',
              benefits: ['Earn smiles', 'Member rates', 'Exclusive deals'],
              color: '#666',
            },
            {
              name: 'Platinum',
              requirements: '15 stays or 30 nights',
              benefits: ['Room upgrades', 'Late checkout', 'Complimentary breakfast'],
              color: '#e5e4e2',
            },
          ],
        },
      ];
    }
  };

  const programs = getPopularPrograms();

  const handleQuickAdd = (program: ProgramTemplate) => {
    setSelectedProgram(program);
    setProgramDetails({
      points: '',
      tier: program.tiers[0].name,
      accountNumber: '',
    });
    setActiveStep(1);
  };

  const handleCustomAdd = () => {
    if (customProgram.name.trim()) {
      const newProgram: ProgramTemplate = {
        name: customProgram.name,
        logo: customProgram.logo || (programType === 'airline' ? '✈️' : '🏨'),
        currency: customProgram.currency,
        color: '#1976d2',
        tiers: [
          {
            name: 'Member',
            requirements: 'Basic membership',
            benefits: ['Earn points', 'Member benefits'],
            color: '#666',
          },
        ],
      };
      setSelectedProgram(newProgram);
      setProgramDetails({
        points: '',
        tier: 'Member',
        accountNumber: '',
      });
      setActiveStep(1);
    }
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleFinish = () => {
    if (selectedProgram) {
      console.log('🔍 AddProgramDialog handleFinish - programType:', programType);
      const newProgram = {
        id: `${programType}_${Date.now()}`,
        name: selectedProgram.name,
        type: programType,
        logo: selectedProgram.logo,
        currency: selectedProgram.currency,
        points: parseInt(programDetails.points) || 0,
        tier: programDetails.tier,
        tierColor: selectedProgram.tiers.find(t => t.name === programDetails.tier)?.color || '#666',
        accountNumber: programDetails.accountNumber,
        color: selectedProgram.color,
        website: `https://www.${selectedProgram.name.toLowerCase().replace(/\s+/g, '')}.com`,
        redemptionUrl: `https://www.${selectedProgram.name.toLowerCase().replace(/\s+/g, '')}.com/rewards`,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      console.log('✅ AddProgramDialog: Finished program creation:', newProgram);
      console.log('✅ AddProgramDialog: Calling onAdd with program type:', programType);
      
      onAdd(newProgram);
      onClose();
      resetDialog();
    }
  };

  const resetDialog = () => {
    setActiveStep(0);
    setSelectedProgram(null);
    setSelectedTier(0);
    setCustomProgram({ name: '', logo: '', currency: 'Points' });
    setProgramDetails({ points: '', tier: '', accountNumber: '' });
  };

  const handleClose = () => {
    onClose();
    resetDialog();
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        const filteredPrograms = programs.filter(p => 
          regionFilter === 'all' || p.region === regionFilter
        );
        
        return (
          <Box sx={{ py: 2 }}>
            {/* Popular Programs */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Popular Programs
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant={regionFilter === 'all' ? 'contained' : 'outlined'}
                  onClick={() => setRegionFilter('all')}
                  sx={{ minWidth: 60, fontSize: '0.75rem', py: 0.5 }}
                >
                  All
                </Button>
                <Button
                  size="small"
                  variant={regionFilter === 'US' ? 'contained' : 'outlined'}
                  onClick={() => setRegionFilter('US')}
                  sx={{ minWidth: 60, fontSize: '0.75rem', py: 0.5 }}
                >
                  US
                </Button>
                <Button
                  size="small"
                  variant={regionFilter === 'India' ? 'contained' : 'outlined'}
                  onClick={() => setRegionFilter('India')}
                  sx={{ minWidth: 60, fontSize: '0.75rem', py: 0.5 }}
                >
                  India
                </Button>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
              {filteredPrograms.map((program, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 2,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'action.hover',
                      transform: 'scale(1.02)',
                    },
                  }}
                  onClick={() => handleQuickAdd(program)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: '1.2rem' }}>{program.logo}</Typography>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="subtitle2">{program.name}</Typography>
                      <Chip
                        size="small"
                        label={program.currency}
                        sx={{
                          bgcolor: program.color,
                          color: 'white',
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Custom Program */}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
              Or Add Custom Program
            </Typography>
            <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'end' }}>
                <TextField
                  label="Program Name"
                  value={customProgram.name}
                  onChange={(e) => setCustomProgram(prev => ({ ...prev, name: e.target.value }))}
                  size="small"
                  sx={{ flexGrow: 1 }}
                />
                <TextField
                  label="Logo/Emoji"
                  value={customProgram.logo}
                  onChange={(e) => setCustomProgram(prev => ({ ...prev, logo: e.target.value }))}
                  size="small"
                  sx={{ width: 100 }}
                  placeholder={programType === 'airline' ? '✈️' : '🏨'}
                />
                <TextField
                  label="Currency"
                  value={customProgram.currency}
                  onChange={(e) => setCustomProgram(prev => ({ ...prev, currency: e.target.value }))}
                  size="small"
                  sx={{ width: 120 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleCustomAdd}
                  disabled={!customProgram.name.trim()}
                >
                  Add
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 1:
        if (!selectedProgram) return null;
        
        const tiers = selectedProgram.tiers;
        
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Program Details
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography sx={{ fontSize: '1.2rem' }}>{selectedProgram.logo}</Typography>
              <Typography variant="subtitle1" fontWeight="bold">
                {selectedProgram.name}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mb: 3 }}>
              <TextField
                fullWidth
                label={`Current ${selectedProgram.currency || 'Points'}`}
                type="number"
                value={programDetails.points}
                onChange={(e) => setProgramDetails(prev => ({ ...prev, points: e.target.value }))}
                inputProps={{ min: 0 }}
              />
              <FormControl fullWidth>
                <InputLabel>Tier Status</InputLabel>
                <Select
                  value={programDetails.tier}
                  label="Tier Status"
                  onChange={(e) => setProgramDetails(prev => ({ ...prev, tier: e.target.value }))}
                >
                  {tiers.map((tier) => (
                    <MenuItem key={tier.name} value={tier.name}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Star sx={{ fontSize: 16, color: tier.color }} />
                        {tier.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <TextField
              fullWidth
              label="Account Number (Optional)"
              value={programDetails.accountNumber}
              onChange={(e) => setProgramDetails(prev => ({ ...prev, accountNumber: e.target.value }))}
              sx={{ mb: 3 }}
            />

            <Typography variant="subtitle2" gutterBottom>
              Tier Benefits
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              {tiers.map((tier, index) => (
                <Box
                  key={index}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: programDetails.tier === tier.name ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    bgcolor: programDetails.tier === tier.name ? 'primary.50' : 'transparent',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: programDetails.tier === tier.name ? 'primary.100' : 'action.hover',
                    },
                  }}
                  onClick={() => setProgramDetails(prev => ({ ...prev, tier: tier.name }))}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star sx={{ fontSize: 16, color: tier.color }} />
                    {tier.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {tier.requirements}
                  </Typography>
                  {tier.benefits.slice(0, 3).map((benefit, bIndex) => (
                    <Typography key={bIndex} variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                      • {benefit}
                    </Typography>
                  ))}
                  {tier.benefits.length > 3 && (
                    <Typography variant="caption" color="primary">
                      +{tier.benefits.length - 3} more
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        );

      case 2:
        if (!selectedProgram) return null;
        
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Confirm Program
            </Typography>
            
            <Box sx={{ p: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography sx={{ fontSize: '2rem' }}>{selectedProgram.logo}</Typography>
                <Box>
                  <Typography variant="h6">{selectedProgram.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {programType.charAt(0).toUpperCase() + programType.slice(1)} Program
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Current Balance
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {parseInt(programDetails.points) || 0} {selectedProgram.currency}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Tier Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star sx={{ fontSize: 16, color: selectedProgram.tiers.find(t => t.name === programDetails.tier)?.color }} />
                    <Typography variant="h6">{programDetails.tier}</Typography>
                  </Box>
                </Box>
              </Box>
              
              {programDetails.accountNumber && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Account Number
                  </Typography>
                  <Typography variant="body1">
                    {programDetails.accountNumber}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Add />
          Add {programType === 'airline' ? 'Airline' : 'Hotel'} Program
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 && selectedProgram && (
          <Button onClick={handleNext} variant="contained">
            Next
          </Button>
        )}
        {activeStep === steps.length - 1 && (
          <Button onClick={handleFinish} variant="contained">
            Add Program
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddProgramDialog;